import type { FileRejection } from "./FileUpload.js";

/* FileUpload's selection rules, split out of FileUpload.tsx so the component
   and its file list share one formatBytes. Pure: no React, no state. */

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const types = accept
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (types.length === 0) return true;
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();
  return types.some((t) => {
    if (t.startsWith(".")) return name.endsWith(t);
    if (t.endsWith("/*")) return mime.startsWith(t.slice(0, -1));
    return mime === t;
  });
}

/** Splits picked files into accepted files and type/size rejections, in the
 *  order they were picked. The count cap is the caller's: it knows the list. */
export function checkFiles(
  files: File[],
  accept: string | undefined,
  maxSize: number | undefined,
): { accepted: File[]; rejected: FileRejection[] } {
  const accepted: File[] = [];
  const rejected: FileRejection[] = [];
  for (const f of files) {
    if (!matchesAccept(f, accept)) {
      rejected.push({
        file: f,
        reason: "type",
        message: `${f.name}: unsupported type.`,
      });
    } else if (maxSize != null && f.size > maxSize) {
      rejected.push({
        file: f,
        reason: "size",
        message: `${f.name}: exceeds ${formatBytes(maxSize)}.`,
      });
    } else {
      accepted.push(f);
    }
  }
  return { accepted, rejected };
}
